export function* blocksIn(page, parents = []) {
  const order = page.blocks_layout?.items;
  if (
    !page.blocks ||
    !Array.isArray(order) ||
    order.length !== Object.keys(page.blocks).length ||
    order.some((id) => typeof id !== 'string')
  ) {
    throw new Error('Invalid block collection');
  }
  for (const id of order) {
    const block = page.blocks[id];
    const entry = { id, block, collection: page, parents };
    yield entry;
    for (const child of [block, block.data]) {
      if (child?.blocks && child.blocks_layout) {
        yield* blocksIn(child, [...parents, entry]);
      }
    }
  }
}

// Check agent changes before they reach the working copy, including changes
// that bypass the chooser and the container editors' insertion guards.
export function validatePage(page) {
  const ids = new Set();
  for (const { id, block, parents } of blocksIn(page)) {
    if (!block || ids.has(id)) {
      throw new Error('Invalid block collection');
    }
    ids.add(id);
    if (parents.length && block['@type'] === 'group') {
      throw new Error('Sections must remain at the top level');
    }
    if (
      ['group', 'columnsBlock', 'accordion'].includes(block['@type']) &&
      (!block.data?.blocks || !block.data?.blocks_layout)
    ) {
      throw new Error('A container needs a block collection');
    }
    if (['gridBlock', 'html'].includes(block['@type'])) {
      throw new Error('This block type is unavailable');
    }
    if (block['@type'] === 'columnsBlock') {
      const columns = Object.values(block.data?.blocks || {});
      if (
        'gridCols' in block ||
        'gridSize' in block ||
        columns.length < 2 ||
        columns.length > 4 ||
        columns.some(
          ({ width }) => !Number.isInteger(width) || width < 1 || width > 3,
        ) ||
        columns.reduce((sum, column) => sum + column.width, 0) > 4
      ) {
        throw new Error('Invalid column widths');
      }
    }
  }
  return page;
}

const textBlock = (text) => ({
  '@type': 'slate',
  disableNewBlocks: true,
  value: [{ type: 'p', children: [{ text }] }],
});

export function buildScenario(
  message,
  initial,
  context,
  newId = () => crypto.randomUUID(),
) {
  const state = structuredClone(initial);
  const steps = [];
  const entries = () => [...blocksIn(state)];
  const selected = () => entries().find(({ id }) => id === context?.block_id);
  const first = (type) =>
    entries().find(({ block }) => block['@type'] === type);
  const emit = (name, description, blockId) => {
    validatePage(state);
    steps.push({ name, description, blockId, state: structuredClone(state) });
  };
  const section = () => {
    const entry = selected();
    return (
      (entry?.block['@type'] === 'group'
        ? entry
        : entry?.parents.find(({ block }) => block['@type'] === 'group')) ||
      first('group')
    );
  };
  const insert = () => {
    const target = section();
    if (!target) throw new Error('Select a page with a Section first.');
    const id = newId();
    target.block.data.blocks[id] = textBlock(
      'This paragraph was added by Lino. You can edit it after this turn.',
    );
    target.block.data.blocks_layout.items.push(id);
    emit('insert_element', 'Add a paragraph inside the Section', id);
  };
  const rewrite = () => {
    const entry = selected();
    const target = entry?.block['@type'] === 'slate' ? entry : first('slate');
    if (!target) throw new Error('There is no text block to edit.');
    const rewriteLeaf = (nodes) => {
      for (const node of nodes || []) {
        if (typeof node.text === 'string' && node.text.trim()) {
          node.text = `Updated by Lino: ${node.text}`;
          return true;
        }
        if (rewriteLeaf(node.children)) return true;
      }
      return false;
    };
    rewriteLeaf(target.block.value);
    delete target.block.plaintext;
    emit(
      'update_element',
      'Update the text, keeping its formatting',
      target.id,
    );
  };
  const swap = () => {
    const entry = selected();
    const target =
      (entry?.block['@type'] === 'columnsBlock'
        ? entry
        : entry?.parents.find(
            ({ block }) => block['@type'] === 'columnsBlock',
          )) || first('columnsBlock');
    if (!target) throw new Error('There are no columns to swap.');
    const items = target.block.data.blocks_layout.items;
    const id = items[0];
    items.push(items.shift());
    emit('move_element', 'Swap the columns, keeping their widths', id);
  };
  const move = (wholeSection = false) => {
    const target = wholeSection ? section() : selected() || section();
    if (!target) throw new Error('Select a block to move.');
    const items = target.collection.blocks_layout.items;
    const from = items.indexOf(target.id);
    const destination = from === items.length - 1 ? 0 : from + 1;
    items.splice(from, 1);
    items.splice(destination, 0, target.id);
    emit(
      'move_element',
      wholeSection ? 'Move the Section' : 'Move the selected block',
      target.id,
    );
  };
  const command = message.toLowerCase();
  if (/fail|fehler/.test(command)) {
    if (/after|nach/.test(command)) insert();
    return { steps, error: true };
  }
  if (/demo/.test(command)) {
    rewrite();
    insert();
    if (first('columnsBlock')) swap();
    move(true);
  } else if (/swap|tausch/.test(command)) swap();
  else if (/rewrite|überarbeit/.test(command)) rewrite();
  else if (/move|verschieb/.test(command))
    move(/section|abschnitt/.test(command));
  else if (/add a section|abschnitt hinzufügen/.test(command)) {
    const id = newId();
    const childId = newId();
    state.blocks[id] = {
      '@type': 'group',
      alignment: 'left',
      background: 'muted',
      data: {
        blocks: { [childId]: textBlock('A new Section from Lino.') },
        blocks_layout: { items: [childId] },
      },
    };
    state.blocks_layout.items.push(id);
    emit('insert_element', 'Add a top-level Section', id);
  } else if (/add|hinzufügen/.test(command)) insert();
  return { steps, error: false };
}
